import { useId, useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck, Plus } from 'lucide-react';
import {
  BASES_DE_MODELO,
  CORES_DAS_BASES,
  falhasDeContraste,
  LIMITE_MODELOS,
  RE_HEX_MODELO,
  TOKENS_BARRA,
  TOKENS_PAGINA,
  variaveisBarra,
  variaveisPagina,
  type BaseDeModelo,
  type CoresTemaModelo,
  type ModeloTema,
} from '@malgoos/shared';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Erro } from '../../componentes/estados';
import { Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';

/**
 * Modelos próprios (03/11, ficha `2026-11-03_malgo_VIS_modos-gelo-mare-e-editor`): o administrador parte de um modo, edita as cores
 * dos dois temas, vê a prévia e o contraste na hora e aplica no workspace. A regra (tokens, hex, contraste) é a de
 * `@malgoos/shared`, a mesma que a API confere ao salvar.
 */

type Tema = 'claro' | 'escuro';
const novoId = () => `m${Array.from(crypto.getRandomValues(new Uint8Array(4)), (b) => b.toString(16).padStart(2, '0')).join('')}`;
const copiar = (c: CoresTemaModelo): CoresTemaModelo => ({ pagina: { ...c.pagina }, barra: { ...c.barra } });

function CampoCor({ rotulo, valor, aoMudar, aoInvalido, desabilitado, erro }: { rotulo: string; valor: string; aoMudar: (v: string) => void; aoInvalido: (invalido: boolean) => void; desabilitado?: boolean; erro: string }) {
  const [texto, setTexto] = useState<string | null>(null);
  const idErro = useId();
  const mostrado = texto ?? valor;
  const invalido = !RE_HEX_MODELO.test(mostrado);
  return (
    <div className="flex min-w-0 flex-col gap-1">
    <div className="flex min-w-0 items-center gap-2" data-campo-cor>
      <span className="min-w-0 flex-1 break-words text-sm text-tinta-2">{rotulo}</span>
      <input
        type="color"
        aria-label={rotulo}
        disabled={desabilitado}
        value={valor.toLowerCase()}
        onChange={(e) => {
          setTexto(null);
          aoInvalido(false);
          aoMudar(e.target.value);
        }}
        className="size-8 shrink-0 cursor-pointer rounded-md border border-borda-campo bg-campo p-0.5"
      />
      <div className="w-28 shrink-0">
        <Entrada
          aria-label={`${rotulo}, hexadecimal`}
          aria-invalid={invalido || undefined}
          aria-describedby={invalido ? idErro : undefined}
          disabled={desabilitado}
          maxLength={7}
          className="numero"
          value={mostrado}
          onChange={(e) => {
            const v = e.target.value.trim();
            setTexto(v);
            aoInvalido(!RE_HEX_MODELO.test(v));
            if (RE_HEX_MODELO.test(v)) aoMudar(v.toLowerCase());
          }}
          onBlur={() => {
            setTexto(null);
            aoInvalido(false);
          }}
        />
      </div>
    </div>
    {invalido && (
      <p id={idErro} className="text-right text-xs text-perigo">
        {erro}
      </p>
    )}
    </div>
  );
}

/** Prévia só com os tokens do sistema: as variáveis do modelo valem dentro destes dois blocos, sem mexer no resto da tela. */
function Previa({ cores }: { cores: CoresTemaModelo }) {
  const { t } = useTranslation('configuracoes');
  return (
    <div data-previa-modelo className="flex min-h-72 min-w-0 overflow-hidden rounded-xl border border-borda" style={variaveisPagina(cores) as CSSProperties}>
      <div className="flex w-28 shrink-0 flex-col gap-1 bg-sup-1 p-2 text-xs text-tinta-2 sm:w-36" style={variaveisBarra(cores) as CSSProperties}>
        <span className="rounded-md border-r-4 border-acento-forte bg-sup-3 px-2 py-1.5 font-semibold text-tinta-1">{t('aparencia.modelos.previaMenu1')}</span>
        <span className="px-2 py-1.5">{t('aparencia.modelos.previaMenu2')}</span>
        <span className="px-2 py-1.5">{t('aparencia.modelos.previaMenu3')}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3 bg-fundo p-3">
        <p className="text-sm font-semibold text-tinta-1">{t('aparencia.modelos.previaTitulo')}</p>
        <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-borda bg-sup-1 p-3">
          <p className="numero text-xl font-semibold text-tinta-1">{t('aparencia.modelos.previaValor')}</p>
          <p className="text-xs text-tinta-3">{t('aparencia.modelos.previaTexto')}</p>
          <div className="flex flex-wrap gap-1.5">
            <Selo cor="verde">{t('aparencia.modelos.previaOk')}</Selo>
            <Selo cor="dourado">{t('aparencia.modelos.previaAtencao')}</Selo>
            <Selo cor="perigo">{t('aparencia.modelos.previaErro')}</Selo>
          </div>
          <span className="text-sm font-medium text-azul">{t('aparencia.modelos.previaLink')}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Botao tamanho="sm" variante="primario" tabIndex={-1}>
            {t('aparencia.modelos.previaBotao')}
          </Botao>
          <Botao tamanho="sm" variante="secundario" tabIndex={-1}>
            {t('aparencia.modelos.previaSecundario')}
          </Botao>
        </div>
      </div>
    </div>
  );
}

function Editor({ inicial, aoSalvar, aoCancelar, salvando }: { inicial: ModeloTema; aoSalvar: (m: ModeloTema) => void; aoCancelar: () => void; salvando: boolean }) {
  const { t, i18n } = useTranslation('configuracoes');
  const [m, setM] = useState(inicial);
  const [tema, setTema] = useState<Tema>('claro');
  const cores = m[tema];
  const [invalidos, setInvalidos] = useState<ReadonlySet<string>>(new Set());
  const marcarInvalido = (chave: string, inv: boolean) =>
    setInvalidos((a) => {
      if (a.has(chave) === inv) return a;
      const n = new Set(a);
      if (inv) n.add(chave);
      else n.delete(chave);
      return n;
    });
  const mudar = (area: 'pagina' | 'barra', token: string, v: string) => setM((a) => ({ ...a, [tema]: { ...a[tema], [area]: { ...a[tema][area], [token]: v } } }));
  const falhas = useMemo(() => (['claro', 'escuro'] as const).flatMap((x) => falhasDeContraste(m[x]).map((f) => ({ ...f, tema: x }))), [m]);
  const nomeTok = (area: string, tk: string) => `${t(`aparencia.modelos.area${area === 'pagina' ? 'Pagina' : 'Barra'}`)}: ${t(`aparencia.modelos.tok.${tk}`)}`;
  return (
    <div className="flex min-w-0 flex-col gap-4 rounded-xl border border-borda p-4" data-editor-modelo>
      <Rotulo id="modelo-nome" rotulo={t('aparencia.modelos.nome')}>
        <Entrada id="modelo-nome" maxLength={40} value={m.nome} onChange={(e) => setM({ ...m, nome: e.target.value })} />
      </Rotulo>
      <div role="radiogroup" aria-label={t('aparencia.modelos.previa')} className="flex flex-wrap gap-2">
        {(['claro', 'escuro'] as const).map((x) => (
          <Botao key={x} tamanho="sm" role="radio" aria-checked={tema === x} variante={tema === x ? 'primario' : 'secundario'} onClick={() => setTema(x)}>
            {t(`aparencia.modelos.${x}`)}
          </Botao>
        ))}
      </div>
      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-4">
          <fieldset className="flex min-w-0 flex-col gap-2">
            <legend className="kicker mb-1">{t('aparencia.modelos.areaPagina')}</legend>
            {TOKENS_PAGINA.map((k) => (
              <CampoCor key={`${tema}-${k}`} rotulo={t(`aparencia.modelos.tok.${k}`)} valor={cores.pagina[k]} erro={t('aparencia.modelos.hexInvalido')} aoMudar={(v) => mudar('pagina', k, v)} aoInvalido={(i) => marcarInvalido(`p-${k}`, i)} />
            ))}
          </fieldset>
          <fieldset className="flex min-w-0 flex-col gap-2">
            <legend className="kicker mb-1">{t('aparencia.modelos.areaBarra')}</legend>
            {TOKENS_BARRA.map((k) => (
              <CampoCor key={`${tema}-b-${k}`} rotulo={t(`aparencia.modelos.tok.${k}`)} valor={cores.barra[k]} erro={t('aparencia.modelos.hexInvalido')} aoMudar={(v) => mudar('barra', k, v)} aoInvalido={(i) => marcarInvalido(`b-${k}`, i)} />
            ))}
          </fieldset>
        </div>
        <div className="flex min-w-0 flex-col gap-3 lg:sticky lg:top-4 lg:self-start">
          <p className="kicker">{t('aparencia.modelos.previa')}</p>
          <Previa cores={cores} />
          {falhas.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-verde" data-contraste-ok>
              <CircleCheck className="size-4 shrink-0" aria-hidden />
              {t('aparencia.modelos.contrasteOk')}
            </p>
          ) : (
            <div className="flex min-w-0 flex-col gap-1 text-sm" role="status" data-contraste-falhas>
              <p className="flex items-center gap-2 font-medium text-perigo">
                <CircleAlert className="size-4 shrink-0" aria-hidden />
                {t('aparencia.modelos.contrasteFalha', { count: falhas.length })}
              </p>
              <ul className="flex max-h-40 flex-col gap-0.5 overflow-auto text-xs text-tinta-2">
                {falhas.map((f) => (
                  <li key={`${f.tema}-${f.area}-${f.frente}-${f.fundo}-${f.cardAgenda ? 'card' : f.selo ? 'selo' : ''}`}>
                    {t(`aparencia.modelos.${f.tema}`)} · {t(f.cardAgenda ? 'aparencia.modelos.contrasteItemCard' : f.selo ? 'aparencia.modelos.contrasteItemSelo' : 'aparencia.modelos.contrasteItem', { frente: f.selo ? t(`aparencia.modelos.tok.${f.frente}`) : nomeTok(f.area, f.frente), fundo: t(`aparencia.modelos.tok.${f.fundo}`), razao: f.razao.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }), minimo: f.minimo.toLocaleString(i18n.language) })}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Botao variante="fantasma" onClick={aoCancelar}>
          {t('aparencia.modelos.cancelar')}
        </Botao>
        <Botao disabled={falhas.length > 0 || invalidos.size > 0 || !m.nome.trim()} carregando={salvando} onClick={() => aoSalvar(m)}>
          {t('aparencia.modelos.salvar')}
        </Botao>
      </div>
    </div>
  );
}

const CHAVE_LISTA = ['configuracoes', 'aparencia', 'modelos'];

export function CartaoModelos() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const podeEditar = podeNoWorkspace(s, 'configuracoes.editar');
  // rcor-1 SEG-B3: a sessão leva só o modelo em uso; a lista completa vem do GET da aparência.
  const lista = useQuery({ queryKey: CHAVE_LISTA, queryFn: () => get<{ modelos?: ModeloTema[] }>('/configuracoes/aparencia') });
  const modelos = useMemo(() => lista.data?.modelos ?? [], [lista.data?.modelos]);
  const ativo = s.aparencia?.estilo_padrao === 'proprio' ? (s.aparencia?.modelo_ativo ?? null) : null;
  const [editando, setEditando] = useState<ModeloTema | null>(null);
  const [base, setBase] = useState<BaseDeModelo>('mare_malgo');
  const [excluir, setExcluir] = useState<ModeloTema | null>(null);
  // rcor-3 ROB3-B2: criar ou alterar vem de como o editor abriu; modelo excluído noutra aba não renasce ao salvar de novo.
  const [ehNovo, setEhNovo] = useState(false);

  const gravar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) => api('PUT', '/configuracoes/aparencia', corpo),
    onSuccess: async () => {
      await Promise.all([s.recarregar(), qc.invalidateQueries({ queryKey: CHAVE_LISTA }), qc.invalidateQueries({ queryKey: ['configuracoes', 'aparencia', 'historico'] }), qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] })]);
    },
    onError: (e) => {
      toast.erro(mensagemDeErro(e));
      void qc.invalidateQueries({ queryKey: CHAVE_LISTA });
    },
  });
  const novo = () => {
    setEhNovo(true);
    setEditando({ id: novoId(), nome: t('aparencia.modelos.nomePadrao'), base, claro: copiar(CORES_DAS_BASES[base].claro), escuro: copiar(CORES_DAS_BASES[base].escuro) });
  };
  const salvar = (m: ModeloTema) => {
    gravar.mutate(
      { modelo_salvar: m, ...(ehNovo && !modelos.some((x) => x.id === m.id) ? { modelo_novo: true } : {}) },
      {
        onSuccess: () => {
          setEditando(null);
          toast.sucesso(t('aparencia.modelos.salvo'));
        },
      },
    );
  };
  return (
    <Cartao titulo={t('aparencia.modelos.titulo')} className="xl:col-span-2">
      <div className="flex flex-col gap-4" data-modelos>
        <p className="text-sm text-tinta-3">{t('aparencia.modelos.ajuda')}</p>
        {lista.isPending && <p className="text-sm text-tinta-3" role="status">{tn('comum.carregando')}</p>}
        {lista.isError && <Erro erro={lista.error} aoTentar={() => void lista.refetch()} />}
        {lista.isSuccess && modelos.length === 0 && !editando && <p className="text-sm text-tinta-3">{t('aparencia.modelos.vazio')}</p>}
        {modelos.length > 0 && (
          <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda">
            {modelos.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-tinta-1">
                  <span className="break-words">{m.nome}</span>
                  {m.id === ativo && <Selo cor="verde">{t('aparencia.modelos.emUso')}</Selo>}
                </span>
                {podeEditar && (
                  <span className="flex flex-wrap gap-2">
                    <Botao tamanho="sm" variante="fantasma" onClick={() => {
                        setEhNovo(false);
                        setEditando(m);
                      }}>
                      {t('aparencia.modelos.editar')}
                    </Botao>
                    <Botao tamanho="sm" variante="fantasma" onClick={() => setExcluir(m)}>
                      {t('aparencia.modelos.excluir')}
                    </Botao>
                    {m.id !== ativo && (
                      <Botao tamanho="sm" variante="secundario" carregando={gravar.isPending} onClick={() => gravar.mutate({ modelo_ativo: m.id, estilo_padrao: 'proprio' }, { onSuccess: () => toast.sucesso(t('aparencia.modelos.aplicado')) })}>
                        {t('aparencia.modelos.aplicar')}
                      </Botao>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {modelos.length > 0 && <p className="text-xs text-tinta-3">{t('aparencia.modelos.aplicarAjuda')}</p>}
        {editando ? (
          <Editor key={editando.id} inicial={editando} salvando={gravar.isPending} aoCancelar={() => setEditando(null)} aoSalvar={salvar} />
        ) : (
          podeEditar &&
          lista.isSuccess &&
          modelos.length < LIMITE_MODELOS && (
            <div className="flex flex-wrap items-end gap-2">
              <Rotulo id="modelo-base" rotulo={t('aparencia.modelos.partida')}>
                <Selecao id="modelo-base" opcoes={BASES_DE_MODELO.map((b) => ({ valor: b, rotulo: tn(`conta.estilo.nomes.${b}`) }))} value={base} onChange={(e) => setBase(e.target.value as BaseDeModelo)} />
              </Rotulo>
              <Botao icone={<Plus className="size-4" aria-hidden />} onClick={novo}>
                {t('aparencia.modelos.novo')}
              </Botao>
            </div>
          )
        )}
        <Confirmar
          aberto={!!excluir}
          aoFechar={() => setExcluir(null)}
          aoConfirmar={() => {
            if (!excluir) return;
            gravar.mutate(
              { modelo_excluir: excluir.id },
              {
                onSuccess: () => {
                  if (editando?.id === excluir.id) setEditando(null);
                  setExcluir(null);
                },
              },
            );
          }}
          carregando={gravar.isPending}
          perigoso
          titulo={t('aparencia.modelos.excluirTitulo')}
          texto={t('aparencia.modelos.excluirTexto')}
        />
      </div>
    </Cartao>
  );
}
